import { useState, useCallback, useRef } from 'react'
import { GenNode } from '../types/carbon/models/GenNode'
import { getNodeID } from '../utils/nodeTypes'
import { 
  ConfigEntity, 
  ConfigType, 
  ConfigData,
  MergedConfigResult 
} from '../types/config'
import { configApi } from '../services/configApi'
import { extractErrorMessage } from '../utils/apitools'
import useSnackbar from './useSnackbar'
import { entityKey } from '../utils/configEntity'

interface UseConfigReturn {
  // State
  configEditorOpen: boolean
  configEntity: ConfigEntity | null
  mergedConfig: MergedConfigResult | null
  loadingConfig: boolean
  savingConfig: boolean
  
  // Actions
  handleNodeSettings: (node: GenNode) => Promise<void>
  handleTableSettings: (tablePath: string) => Promise<void>
  handleSaveConfig: (data: ConfigData) => Promise<void>
  saveConfigFor: (entity: ConfigEntity, data: ConfigData) => Promise<void>
  openConfigEntity: (entity: ConfigEntity) => Promise<void>
  closeConfigEditor: () => void
}

const DEFAULT_ROUTE_JOB = '__root__'

export const useConfig = (
  customer?: string | null,
  job?: string | null,
  onConfigSaved?: () => void | Promise<void>
): UseConfigReturn => {
  const snackbar = useSnackbar()
  // ConfigEditor state
  const [configEditorOpen, setConfigEditorOpen] = useState(false)
  const [configEntity, setConfigEntity] = useState<ConfigEntity | null>(null)
  const [mergedConfig, setMergedConfig] = useState<MergedConfigResult | null>(null)
  const [loadingConfig, setLoadingConfig] = useState(false)
  const [savingConfig, setSavingConfig] = useState(false)
  // The entity the user chose last. A load that finishes after the user has
  // moved on is dropped, so the level picker and the form never disagree
  // (#1478: switching level while a save was in flight showed one entity's
  // text under another's name, and the next edit saved to the wrong one).
  const latestEntityKeyRef = useRef('')
  const openSeqRef = useRef(0)

  const openConfigEntity = useCallback(async (entity: ConfigEntity): Promise<void> => {
    const targetCustomer = entity.customer || customer
    if (!targetCustomer) {
      console.warn('Attempted to open config without a customer context')
      return
    }

    const routeJob = entity.job || job || DEFAULT_ROUTE_JOB

    const resolvedEntity: ConfigEntity = {
      ...entity,
      customer: targetCustomer,
      display_name: entity.display_name || entity.table_name || entity.folder_path || targetCustomer || 'Configuration'
    }

    setConfigEditorOpen(true)
    latestEntityKeyRef.current = entityKey(resolvedEntity)
    const seq = ++openSeqRef.current

    try {
      setLoadingConfig(true)
      const merged = await configApi.getMergedConfig(targetCustomer, routeJob, {
        config_type: resolvedEntity.type,
        customer: resolvedEntity.customer,
        job: resolvedEntity.job,
        folder_path: resolvedEntity.folder_path,
        table_name: resolvedEntity.table_name
      })
      if (seq !== openSeqRef.current) return
      // Set entity and its merged config together, in the same tick. Setting
      // configEntity synchronously (before this await) used to let ConfigEditor
      // re-render with the NEW entity paired with the PREVIOUS entity's
      // (stale) mergedConfig; its autoSave "don't clobber in-progress edits"
      // guard then locked onto that stale pairing and permanently discarded
      // the correct fetch below, since it looked like a no-op refresh for an
      // already-processed entity. That hid freshly-fetched scope-specific
      // fields (e.g. a job-level custom persona) after switching scope.
      setConfigEntity(resolvedEntity)
      setMergedConfig(merged)
    } catch (err) {
      console.error('Error loading merged config:', err)
    } finally {
      if (seq === openSeqRef.current) setLoadingConfig(false)
    }
  }, [customer, job])

  // Configuration handler functions
  const handleNodeSettings = useCallback(async (node: GenNode) => {
    if (!customer || !job) return

    const isFolder = node.type === 'Folder'
    const nodeId = getNodeID(node)

    const entity: ConfigEntity = {
      type: isFolder ? ConfigType.Folder : ConfigType.Table,
      customer,
      job,
      folder_path: isFolder ? nodeId : nodeId.substring(0, nodeId.lastIndexOf('/')) || '/',
      table_name: isFolder ? undefined : node.value1 || undefined,
      display_name: isFolder ? `Folder: ${nodeId}` : `Table: ${node.value1}`,
      node
    }

    await openConfigEntity(entity)
  }, [customer, job, openConfigEntity])

  const handleTableSettings = useCallback(async (tablePath: string) => {
    if (!customer || !job || !tablePath) return

    const folderPath = tablePath.substring(0, tablePath.lastIndexOf('/')) || '/'
    const tableName = tablePath.split('/').pop()?.replace(/\.cbt$/, '') || ''

    const entity: ConfigEntity = {
      type: ConfigType.Table,
      customer,
      job,
      folder_path: folderPath,
      table_name: tableName,
      display_name: `Table: ${tableName}`
    }

    await openConfigEntity(entity)
  }, [customer, job, openConfigEntity])

  // Saves to the entity named, whatever the editor last opened.
  const saveConfigFor = useCallback(async (configEntity: ConfigEntity | null, data: ConfigData): Promise<void> => {
    if (!configEntity || !configEntity.customer) {
      throw new Error('No entity selected or missing customer/job')
    }

    const entityToRefresh = { ...configEntity }

    try {
      setSavingConfig(true)

      // Create the config object
      const configToSave = {
        config_type: configEntity.type,
        customer: configEntity.customer,
        job: configEntity.job,
        folder_path: configEntity.folder_path,
        table_name: configEntity.table_name,
        data
      }

      const routeJob = configEntity.job || job || DEFAULT_ROUTE_JOB

      // Try to find existing config first
      const existingConfigs = await configApi.listConfigs(configEntity.customer, routeJob, {
        config_type: configEntity.type,
        customer: configEntity.customer,
        job: configEntity.job,
        folder_path: configEntity.folder_path,
        table_name: configEntity.table_name
      })

      if (existingConfigs.length > 0) {
        // Update existing config
        await configApi.updateConfig(configEntity.customer, routeJob, existingConfigs[0].id, configToSave)
      } else {
        // Create new config
        await configApi.createConfig(configEntity.customer, routeJob, configToSave)
      }

      snackbar.success('Configuration saved')

      // Call the onConfigSaved callback if provided
      if (onConfigSaved) {
        await onConfigSaved()
      }

      // Refresh only if the user is still on the entity that was saved.
      if (latestEntityKeyRef.current === entityKey(entityToRefresh)) {
        await openConfigEntity(entityToRefresh)
      }

    } catch (err) {
      const errorMessage = extractErrorMessage(err)
      console.error(`Failed to save configuration: ${errorMessage}`)
      snackbar.error(errorMessage)
      throw err // Re-throw so the modal can handle it
    } finally {
      setSavingConfig(false)
    }
  }, [job, onConfigSaved, openConfigEntity])

  const handleSaveConfig = useCallback(
    (data: ConfigData) => saveConfigFor(configEntity, data),
    [configEntity, saveConfigFor],
  )

  const closeConfigEditor = useCallback(() => {
    setConfigEditorOpen(false)
    setConfigEntity(null)
    setMergedConfig(null)
  }, [])

  return {
    // State
    configEditorOpen,
    configEntity,
    mergedConfig,
    loadingConfig,
    savingConfig,
    
    // Actions
    handleNodeSettings,
    handleTableSettings,
    handleSaveConfig,
    saveConfigFor,
    openConfigEntity,
    closeConfigEditor
  }
}

export default useConfig
